<template>
    <div id="urls-input">
        <textarea
            v-model="url"
            @blur="onBlurUrl"
            :class="{
                'font-size-16 md:font-size-20': true,
                'form-control mb-1': isBackend,
                'over-limit': limitExceeded
            }"
            :placeholder="translate('robot_modal_url_placeholder', 'Enter one or more URLs (separated by newline)')"
            id="urls-textarea"
            rows="4"
        ></textarea>
        <div class="validate-box">
            <div class="flex d-flex">
                <div v-if="invalidUrlsCount > 0" class="badge-text invalid-url mr-2">
                    {{ translate('robot_modal_invalid_url', 'Invalid URL') }}: {{ invalidUrlsCount }}
                </div>
                <div v-if="validUrlsCount > 0" class="badge-text valid-url">
                    {{ translate('robot_modal_valid_url', 'Valid URL') }}: {{ validUrlsCount }}
                </div>
            </div>
            <div>
                <div v-if="limitExceeded" class="badge-text limit-url ml-2">
                    {{ translate('robot_modal_limit_url', `Maximum ${limitUrls} URLs`, { limit: limitUrls }) }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { debounce } from 'lodash'

export default {
    name: 'URLsInput',

    data() {
        return {
            url: '',
            invalidUrlsCount: 0,
            validUrlsCount: 0,
            limit: {
                teacher_plus: 5,
                member: 2
            }
        }
    },

    computed: {
        isBackend() {
            return process.env.VUE_APP_IS_BACKEND
        },

        limitUrls() {
            if (this.isBackend) {
                return 10
            }

            const user = this.getUser()

            return this.limit[user.role] || this.limit.member
        },

        limitExceeded() {
            return this.validUrlsCount + this.invalidUrlsCount > this.limitUrls
        }
    },

    watch: {
        url(newValue) {
            const urlArray = newValue.split('\n').map(url => url.trim())
            const uniqueUrls = new Set(urlArray)
            this.url = Array.from(uniqueUrls).join('\n')
            this.debouncedValidateUrls(this.url)
        }
    },

    methods: {
        translate(key, msg, variables) {
            if (this.isBackend) {
                return msg
            }

            return this.$t(key, variables)
        },

        onBlurUrl(event) {
            if (event.target.value) {
                const urlArray = event.target.value.split('\n').map(url => url.trim().replace(/\/+$/, ''))
                const uniqueUrls = new Set(urlArray)
                this.url = Array.from(uniqueUrls).join('\n')
                this.debouncedValidateUrls(this.url)
            }
        },

        validateUrls(urls) {
            const urlArray = urls.split('\n').map(url => url.trim().replace(/\/+$/, ''))

            this.invalidUrlsCount = 0
            this.validUrlsCount = 0

            const validUrls = []

            urlArray.forEach(url => {
                if (url !== '') {
                    if (this.isValidUrl(url)) {
                        this.validUrlsCount++

                        validUrls.push(url)
                    } else {
                        this.invalidUrlsCount++
                    }
                }
            })

            const invalid = this.invalidUrlsCount > 0 || this.limitExceeded

            //check duplicate of validUrls
            const uniqueUrls = new Set(validUrls)
            validUrls.splice(0, validUrls.length)
            validUrls.push(...uniqueUrls)

            this.$emit('urls', validUrls)
            this.$emit('invalid', invalid)
        },

        isValidUrl(url) {
            if (url.includes(' ')) {
                return false
            }

            const urlRegex = /^(https?:\/\/)?([\w.-]+\.[a-z]{2,})([/?#].*)?$/i
            return urlRegex.test(url)
        },

        debouncedValidateUrls: debounce(function (urls) {
            this.validateUrls(urls)
        }, 300)
    }
}
</script>

<style lang="scss" scoped>
#urls-input {
    #urls-textarea {
        font-family: 'Open Sans', sans-serif;
        padding: 0.375rem 0.75rem;
        border: 1px solid #ccc;
        font-size: 1rem;
        font-weight: 400;
        color: #000;
        text-shadow: none;
        width: 100%;
        border-radius: 4px;
        resize: none;
        height: 102px;
        text-align: left;

        &::placeholder {
            text-align: left;
        }

        &.over-limit {
            border-color: #e3342f;
        }
    }

    .validate-box {
        display: flex;
        justify-content: space-between;

        .badge-text {
            font-size: 0.75rem;
            font-weight: bold;
            color: #fff;
            padding: 0.25rem 0.4rem;
            border-radius: 0.25rem;
            display: inline-block;
        }

        .invalid-url {
            background-color: #e3342f;
        }

        .valid-url {
            background-color: #38c172;
        }

        .limit-url {
            background-color: #e3342f;
        }
    }
}
</style>
